import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { Store } from '@ngrx/store';
import { NumpadSource, StakeChangedEvent } from 'packages/sports/web/app/src/numpad/model';
import { NumpadDisplayService } from 'packages/sports/web/app/src/numpad/numpad-display.service';
import { NumpadService } from 'packages/sports/web/app/src/numpad/numpad.service';
import { Observable, combineLatest, distinctUntilChanged, map, merge, startWith } from 'rxjs';

import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { PickId } from '../../../../core/picks/pick-id';
import { FreeBetToken, RewardTokenContext } from '../../../reward-tokens/reward-tokens.model';
import { selectSinglePickStakeErrorStateFactory } from '../../../validation/selectors';
import { BETSLIP_LINEAR_NUMPAD } from '../../models';
import StakeConvertService from '../../services/stake-convert.service';
import { validateStake } from '../../utils';

@Component({
    selector: 'bs-digital-single-bet-pick-stake',
    templateUrl: './single-bet-pick-stake.component.html',
    providers: [NumpadService],
    styleUrls: ['./single-bet-pick-stake.component.scss'],
    // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export default class SingleBetPickStakeComponent implements OnInit, OnDestroy {
    @Input() pickId: PickId;
    @Input() stake: number | null;
    @Input() isDisabled: boolean;
    @Input() freeBetToken: FreeBetToken | null;
    @Output() stakeChange = new EventEmitter<string>();
    @HostBinding('class') className = 'single-bet-pick-stake';

    hasStakeError$: Observable<boolean>;
    numpadSource = NumpadSource.BetSlip;

    constructor(
        private stakeConvert: StakeConvertService,
        private numpadDisplayService: NumpadDisplayService,
        private betslipConfig: BetslipConfig,
        private store: Store<IBetslipRootState>,
    ) {}

    ngOnInit(): void {
        const state$ = this.store.select(selectSinglePickStakeErrorStateFactory(this.pickId));

        const numpadClosed$ = this.numpadDisplayService.close$.pipe(map(() => false));
        const numpadOpened$ = this.numpadDisplayService.open$.pipe(map(() => true));

        const numpadOpen$ = merge(numpadOpened$, numpadClosed$).pipe(startWith(false), distinctUntilChanged());

        this.hasStakeError$ = combineLatest([state$, numpadOpen$]).pipe(
            map(([{ stakeError, stake, showInsufficientFundsError }, isNumpadOpen]) => {
                return showInsufficientFundsError || !!validateStake(stakeError, stake?.actualStake ?? null, isNumpadOpen);
            }),
        );
    }

    setStake(event: StakeChangedEvent) {
        this.stakeChange.emit(event.stake);
    }

    get stakeAsStr(): string {
        return this.stakeConvert.convert(this.stake);
    }

    get tokenContext(): RewardTokenContext {
        return {
            betslipType: BetslipType.Single,
            pickId: this.pickId,
        };
    }

    /**
     * Keypad can be either multiple inline keypads (per pick) or shared one in the summary
     */
    get keypadId(): string {
        return this.betslipConfig.isLinearKeypadEnabled ? BETSLIP_LINEAR_NUMPAD : this.pickId.toString();
    }

    ngOnDestroy() {
        this.numpadDisplayService.tryClose(this.pickId.toString());
    }
}
